import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifySessionExpired } from 'core/api/sessionEvents';
import { queryKeys } from 'core/api/queryKeys';
import { captureRevision, setExpectedRevision } from 'core/api/revisionStore';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import {
  CLIENT_MAX_UPLOAD_BYTES,
  WORKBOOK_ACCEPT,
  filterWorkbookErrors,
  invalidateNewQuestionsQueries,
  isExpiredNewQuestionsPreview,
  isStaleNewQuestionsTemplate,
  hasNewQuestionsCellErrors,
  newQuestionsPreviewFromApiError,
  newQuestionsSummaryRows,
  normalizeNewQuestionsPreview,
  saveBlobFile,
  validateWorkbookFile,
} from 'features/workbooks/workbook';
import { useBodyScrollLock, useFocusTrap } from 'layout/useFocusTrap';
import { Button } from 'shared/components/Button';
import { Badge } from 'shared/components/Feedback';
import { SwitchField } from 'shared/forms/Fields';
import { formatFileSize } from 'shared/utilities/format';

export function NewQuestionsImportDialog({
  open,
  onClose,
  restoreFocusRef,
  canImport,
  onSuccess,
}) {
  const { t, locale, dir } = useI18n();
  const queryClient = useQueryClient();
  const dialogRef = useRef(null);
  const fileInputRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const inflightRef = useRef(false);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [topError, setTopError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [expiredPreview, setExpiredPreview] = useState(false);
  const [result, setResult] = useState(null);

  const requestInFlight = Boolean(busy);
  const staleTemplate = Boolean(preview?.templateStale || preview?.patternSetStale);
  const filteredErrors = useMemo(
    () => filterWorkbookErrors(preview?.errors || [], ''),
    [preview],
  );
  const summaryRows = useMemo(() => newQuestionsSummaryRows(result || preview), [preview, result]);

  const resetAll = useCallback(() => {
    setStep(0);
    setFile(null);
    setFileError('');
    setPreview(null);
    setConfirmed(false);
    setBusy(null);
    setUploadProgress(null);
    setStatusMessage('');
    setTopError('');
    setForbidden(false);
    setExpiredPreview(false);
    setResult(null);
    inflightRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const closeDialog = useCallback(() => {
    if (inflightRef.current) {
      return;
    }
    resetAll();
    onClose();
    const previous = restoreFocusRef?.current;
    if (previous && typeof previous.focus === 'function') {
      previous.focus();
    }
  }, [onClose, resetAll, restoreFocusRef]);

  useFocusTrap(open, dialogRef, {
    onEscape: () => {
      if (!inflightRef.current) {
        closeDialog();
      }
    },
    restore: false,
  });
  useBodyScrollLock(open);

  const applyFile = (nextFile) => {
    if (!nextFile) {
      return;
    }
    const check = validateWorkbookFile(nextFile, { maxBytes: CLIENT_MAX_UPLOAD_BYTES });
    if (!check.ok) {
      setFileError(check.code === 'oversized' ? t('workbook.errors.oversized') : t('workbook.newQuestions.unsupportedFile'));
      return;
    }
    setFile(nextFile);
    setFileError('');
    setPreview(null);
    setConfirmed(false);
    setExpiredPreview(false);
    setTopError('');
    setResult(null);
    setStep(0);
  };

  const clearFile = () => {
    setFile(null);
    setFileError('');
    setPreview(null);
    setConfirmed(false);
    setExpiredPreview(false);
    setTopError('');
    setStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const previewMutation = useMutation({
    mutationKey: queryKeys.workbooks.newQuestionsPreview,
    mutationFn: ({ nextFile, config }) => assessmentApi.previewNewQuestionsImport(nextFile, config),
  });

  const commitMutation = useMutation({
    mutationKey: queryKeys.workbooks.newQuestionsCommit,
    mutationFn: ({ nextFile, token }) => assessmentApi.commitNewQuestionsImport(nextFile, token),
  });

  const progressConfig = {
    onUploadProgress: (event) => {
      if (event?.total) {
        setUploadProgress(Math.round((event.loaded / event.total) * 100));
      } else {
        setUploadProgress(null);
      }
    },
  };

  const handlePreviewFailure = (error) => {
    if (error instanceof ApiError && error.isUnauthorized()) {
      notifySessionExpired();
      return;
    }
    if (error instanceof ApiError && error.isForbidden()) {
      setForbidden(true);
      setTopError(t('errors.forbidden'));
      return;
    }
    if (isExpiredNewQuestionsPreview(error)) {
      setExpiredPreview(true);
      setPreview((current) => (current ? { ...current, canCommit: false, commitToken: '' } : current));
      setTopError(t('workbook.newQuestions.errors.expiredPreview'));
      return;
    }
    if (error instanceof ApiError && error.isConflict()) {
      const next = newQuestionsPreviewFromApiError(error);
      setPreview(next);
      setTopError(isStaleNewQuestionsTemplate(next, error)
        ? t('workbook.newQuestions.staleTemplate')
        : humanizeApiError(error, t));
      requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }
    if (error instanceof ApiError && error.isValidation() && hasNewQuestionsCellErrors(error)) {
      const next = newQuestionsPreviewFromApiError(error);
      setPreview(next);
      setTopError(
        isStaleNewQuestionsTemplate(next, error)
          ? t(next.patternSetStale ? 'workbook.newQuestions.patternSetChanged' : 'workbook.newQuestions.staleTemplate')
          : (humanizeApiError(error, t) || t('workbook.errors.validationTitle')),
      );
      requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }
    if (error instanceof ApiError && (
      error.isPayloadTooLarge()
      || error.isUnsupportedMedia()
      || error.isBadRequest()
      || error.isValidation()
      || error.status >= 500
    )) {
      setPreview(null);
      setTopError(humanizeApiError(error, t) || t('workbook.newQuestions.failure'));
      requestAnimationFrame(() => errorSummaryRef.current?.focus());
      return;
    }
    if (error instanceof ApiError && error.isThrottled()) {
      setTopError(t('errors.throttled'));
      return;
    }
    if (error instanceof ApiError && error.isNetworkError) {
      setTopError(t('workbook.errors.networkPreview'));
      return;
    }
    setTopError(humanizeApiError(error, t) || t('workbook.newQuestions.failure'));
  };

  const runPreview = async () => {
    if (!file || !canImport || inflightRef.current || forbidden) {
      return;
    }
    inflightRef.current = true;
    setBusy('preview');
    setStatusMessage(t('workbook.newQuestions.previewing'));
    setTopError('');
    setExpiredPreview(false);
    setStep(1);
    try {
      const data = await previewMutation.mutateAsync({ nextFile: file, config: progressConfig });
      const next = normalizeNewQuestionsPreview(data);
      setPreview(next);
      if (next.errors.length > 0 || !next.valid || !next.canCommit) {
        setTopError(next.message || (
          next.templateStale
            ? t(next.patternSetStale ? 'workbook.newQuestions.patternSetChanged' : 'workbook.newQuestions.staleTemplate')
            : t('workbook.errors.validationTitle')
        ));
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
      } else {
        setStep(2);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.info('new-questions preview failed', {
          status: error instanceof ApiError ? error.status : null,
          code: error instanceof ApiError ? error.code : null,
          message: error instanceof ApiError ? error.message : String(error?.message || error),
          filename: file?.name || null,
          fileType: file?.type || null,
          fileSize: file?.size ?? null,
        });
      }
      handlePreviewFailure(error);
    } finally {
      inflightRef.current = false;
      setBusy(null);
      setUploadProgress(null);
      setStatusMessage('');
    }
  };

  const runCommit = async () => {
    if (!file || !preview?.canCommit || !preview.commitToken || inflightRef.current || expiredPreview || staleTemplate || !confirmed) {
      return;
    }
    inflightRef.current = true;
    setBusy('commit');
    setStatusMessage(t('workbook.status.committing'));
    setTopError('');
    try {
      const data = await commitMutation.mutateAsync({ nextFile: file, token: preview.commitToken });
      const completed = normalizeNewQuestionsPreview(data);
      if (completed.revision) {
        setExpectedRevision(completed.revision);
      }
      captureRevision(data);
      await invalidateNewQuestionsQueries(queryClient);
      if (typeof onSuccess === 'function') {
        await onSuccess(completed);
      }
      setResult(completed);
      setFile(null);
      setPreview(null);
      setStep(3);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        notifySessionExpired();
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        setForbidden(true);
        setTopError(t('errors.forbidden'));
        return;
      }
      if (isExpiredNewQuestionsPreview(error)) {
        setExpiredPreview(true);
        setPreview((current) => (current ? { ...current, canCommit: false, commitToken: '' } : current));
        setTopError(t('workbook.newQuestions.errors.expiredPreview'));
        return;
      }
      if (error instanceof ApiError && error.isConflict()) {
        const next = newQuestionsPreviewFromApiError(error);
        setPreview((current) => (current
          ? { ...current, ...next, canCommit: false, commitToken: '' }
          : next));
        setTopError(isStaleNewQuestionsTemplate(next, error)
          ? t('workbook.newQuestions.staleTemplate')
          : humanizeApiError(error, t));
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        const next = newQuestionsPreviewFromApiError(error);
        setPreview(next);
        setStep(1);
        setTopError(humanizeApiError(error, t));
        requestAnimationFrame(() => errorSummaryRef.current?.focus());
        return;
      }
      if (error instanceof ApiError && error.isThrottled()) {
        setTopError(t('errors.throttled'));
        return;
      }
      setTopError(humanizeApiError(error, t) || t('workbook.newQuestions.failure'));
    } finally {
      inflightRef.current = false;
      setBusy(null);
      setUploadProgress(null);
      setStatusMessage('');
    }
  };

  const downloadFreshTemplate = async () => {
    if (inflightRef.current) {
      return;
    }
    inflightRef.current = true;
    setBusy('download');
    setStatusMessage(t('workbook.newQuestions.downloadBusy'));
    try {
      const downloaded = await assessmentApi.downloadNewQuestionsTemplate();
      saveBlobFile(downloaded.blob, downloaded.filename);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        notifySessionExpired();
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        setForbidden(true);
        setTopError(t('errors.forbidden'));
        return;
      }
      setTopError(humanizeApiError(error, t) || t('workbook.errors.downloadFailed'));
    } finally {
      inflightRef.current = false;
      setBusy(null);
      setStatusMessage('');
    }
  };

  if (!open) {
    return null;
  }

  const canCommit = Boolean(
    canImport
    && file
    && preview?.canCommit
    && preview.commitToken
    && preview.errors.length === 0
    && !staleTemplate
    && !expiredPreview
    && !forbidden
    && confirmed,
  );

  return (
    <div
      className="modal-backdrop"
      data-testid="new-questions-import-backdrop"
      role="presentation"
      onClick={() => {
        if (!requestInFlight) {
          closeDialog();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="modal modal-wide workbook-import-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-questions-import-title"
        aria-describedby="new-questions-import-description"
        tabIndex={-1}
        dir={dir}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div className="toolbar wrap modal-header-row">
            <h2 id="new-questions-import-title">{t('workbook.newQuestions.importTitle')}</h2>
            <Button tone="ghost" onClick={closeDialog} disabled={requestInFlight}>{t('actions.close')}</Button>
          </div>
          <p id="new-questions-import-description" className="muted">{t('workbook.newQuestions.dialogDescription')}</p>
        </div>
        <div className="modal-body" data-testid="new-questions-modal-body">
          <div className="sr-only" aria-live="polite">{statusMessage}</div>
          {topError ? (
            <p ref={errorSummaryRef} className="form-banner" role="alert" tabIndex={-1}>
              {topError}
            </p>
          ) : null}
          {forbidden ? (
            <p className="warning-banner" role="alert">{t('errors.forbidden')}</p>
          ) : null}
          {staleTemplate || expiredPreview ? (
            <div className="warning-banner" role="alert">
              <p>
                {expiredPreview
                  ? t('workbook.newQuestions.errors.expiredPreview')
                  : t(preview?.patternSetStale ? 'workbook.newQuestions.patternSetChanged' : 'workbook.newQuestions.staleTemplate')}
              </p>
              <Button
                tone="secondary"
                onClick={downloadFreshTemplate}
                loading={busy === 'download'}
                disabled={requestInFlight}
              >
                {t('workbook.newQuestions.downloadFresh')}
              </Button>
            </div>
          ) : null}

          {step < 3 ? (
            <div className="stack-form">
              <p className="muted">{t('workbook.newQuestions.securityNotice')}</p>
              <div className="toolbar wrap">
                <Button
                  tone="secondary"
                  onClick={downloadFreshTemplate}
                  loading={busy === 'download'}
                  disabled={requestInFlight}
                >
                  {t('workbook.newQuestions.downloadTemplate')}
                </Button>
              </div>
              <label className="field" htmlFor="new-questions-file-input">
                <span className="field-label">{t('workbook.newQuestions.selectFile')}</span>
              </label>
              <input
                ref={fileInputRef}
                id="new-questions-file-input"
                className="input"
                type="file"
                accept={WORKBOOK_ACCEPT}
                onChange={(event) => applyFile(event.target.files?.[0])}
                disabled={requestInFlight || forbidden}
              />
              <p className="muted">{t('workbook.fileRestrictions')}</p>
              {fileError ? <p className="field-error" role="alert">{fileError}</p> : null}
              {file ? (
                <div className="file-meta-card">
                  <div className="file-meta-copy">
                    <strong className="file-meta-name">{file.name}</strong>
                    <span className="muted">{formatFileSize(file.size, locale)}</span>
                  </div>
                  <div className="toolbar wrap">
                    <Button
                      tone="secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={requestInFlight}
                    >
                      {t('workbook.newQuestions.replaceFile')}
                    </Button>
                    <Button tone="ghost" onClick={clearFile} disabled={requestInFlight}>
                      {t('workbook.newQuestions.clearFile')}
                    </Button>
                  </div>
                </div>
              ) : null}
              {busy === 'preview' || busy === 'commit' ? (
                <div className="progress-bar" role="status">
                  {uploadProgress != null
                    ? t('workbook.status.uploadProgress', { percent: uploadProgress })
                    : t('workbook.status.working')}
                </div>
              ) : null}
              {preview ? (
                <NewQuestionsPreview
                  t={t}
                  preview={preview}
                  summaryRows={summaryRows}
                  errors={filteredErrors}
                  dir={dir}
                />
              ) : null}
              {step === 2 && preview?.canCommit ? (
                <div className="warning-banner">
                  <p>{t('workbook.newQuestions.confirmCopy')}</p>
                  <p className="muted">{t('workbook.newQuestions.existingUnchanged')}</p>
                  <SwitchField
                    id="new-questions-append-confirm"
                    label={t('workbook.newQuestions.confirmLabel')}
                    checked={confirmed}
                    onChange={setConfirmed}
                    disabled={requestInFlight}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="stack-form">
              <p className="success-banner" role="status">
                {t('workbook.newQuestions.success', {
                  questions: result?.questionsCount ?? 0,
                  answers: result?.answersCount ?? 0,
                })}
              </p>
              <p>{t('workbook.newQuestions.createdQuestions')}: {result?.questionsCount ?? 0}</p>
              <p>{t('workbook.newQuestions.createdAnswers')}: {result?.answersCount ?? 0}</p>
              <SummaryTable t={t} rows={summaryRows} />
              <Button onClick={closeDialog}>{t('actions.close')}</Button>
            </div>
          )}
        </div>
        {step < 3 ? (
          <div className="modal-footer">
            <div className="modal-actions">
              <Button tone="ghost" onClick={closeDialog} disabled={requestInFlight}>{t('actions.cancel')}</Button>
              <Button
                onClick={runPreview}
                loading={busy === 'preview'}
                disabled={!file || requestInFlight || Boolean(fileError) || forbidden || !canImport}
              >
                {busy === 'preview' ? t('workbook.newQuestions.previewing') : t('workbook.newQuestions.preview')}
              </Button>
              <Button
                data-testid="new-questions-commit"
                onClick={runCommit}
                loading={busy === 'commit'}
                disabled={!canCommit || requestInFlight}
              >
                {t('workbook.newQuestions.commit')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function NewQuestionsPreview({ t, preview, summaryRows, errors, dir }) {
  return (
    <div className="workbook-preview">
      <h3>{t('workbook.newQuestions.previewSummary')}</h3>
      <dl className="meta-grid">
        <div>
          <dt>{t('workbook.newQuestions.previewState')}</dt>
          <dd>
            <Badge tone={preview.valid ? 'success' : 'danger'}>
              {preview.valid ? t('workbook.newQuestions.valid') : t('workbook.newQuestions.invalid')}
            </Badge>
          </dd>
        </div>
        <div>
          <dt>{t('workbook.workbookType')}</dt>
          <dd>{preview.workbookType || '—'}</dd>
        </div>
        <div>
          <dt>{t('workbook.newQuestions.templateVersion')}</dt>
          <dd data-testid="new-questions-template-version">{preview.templateVersion || preview.schemaVersion || '—'}</dd>
        </div>
      </dl>
      <SummaryTable t={t} rows={summaryRows} />
      {preview.questions.length > 0 ? (
        <section>
          <h3>{t('workbook.newQuestions.questionSummaries')}</h3>
          <div className="table-wrap workbook-error-scroll" dir={dir}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('workbook.newQuestions.questionRef')}</th>
                  <th>{t('workbook.newQuestions.sectionAr')}</th>
                  <th>{t('workbook.newQuestions.answerCount')}</th>
                  <th>{t('workbook.newQuestions.weightsComplete')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.questions.map((question) => (
                  <tr key={question.key}>
                    <td className="file-meta-name">{question.questionRef || '—'}</td>
                    <td className="file-meta-name">{question.sectionAr || '—'}</td>
                    <td>{question.answerCount}</td>
                    <td>
                      <Badge tone={question.weightsComplete ? 'success' : 'warning'}>
                        {question.weightsComplete ? t('workbook.yes') : t('workbook.no')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {(preview.warnings || []).length > 0 ? (
        <section>
          <h3>{t('workbook.warnings')}</h3>
          <ul className="warning-list" data-testid="new-questions-warnings">
            {preview.warnings.map((warning, index) => (
              <li key={`${warning.code}-${index}`} className="workbook-warning-item">
                <Badge tone="warning">{warning.code || t('workbook.warning')}</Badge>
                <span>{warning.message}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {errors.length > 0 ? (
        <section>
          <h3>{t('workbook.errors.tableTitle')}</h3>
          <div className="table-wrap workbook-error-scroll" dir={dir}>
            <table className="data-table workbook-error-table" data-testid="new-questions-error-table">
              <thead>
                <tr>
                  <th>{t('workbook.newQuestions.excelRow')}</th>
                  <th>{t('workbook.newQuestions.column')}</th>
                  <th>{t('workbook.newQuestions.questionRef')}</th>
                  <th>{t('workbook.errors.message')}</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((error) => (
                  <tr key={`${error.order}-${error.cell}`} className="workbook-error-row">
                    <td>{error.row || '—'}</td>
                    <td>{[error.sheet, error.column || error.cell].filter(Boolean).join(' / ') || '—'}</td>
                    <td className="file-meta-name">{error.questionRef || '—'}</td>
                    <td>{error.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function SummaryTable({ t, rows }) {
  return (
    <div className="workbook-summary-grid" role="table" aria-label={t('workbook.changeSummary')}>
      {rows.map((row) => (
        <div
          key={row.key}
          className={`workbook-summary-row${row.value === 0 ? ' is-zero' : ''}`}
          role="row"
          data-testid={`new-questions-count-${row.key}`}
        >
          <span className="workbook-summary-label" role="rowheader">{t(row.key)}</span>
          <span className="workbook-summary-value" role="cell">{row.value}</span>
        </div>
      ))}
    </div>
  );
}
